const ENTITY_TYPES = Object.freeze({
  animal: Object.freeze({
    accessKeys: ["ANIMAL_MANAGEMENT"],
    category: "animals",
    destinationId: "collection.individual",
    icon: "animals",
    v1: (id) => `/AnimalManagementView?animalId=${encodeURIComponent(id)}`,
    v2: (id) => `/v2/individuals/${encodeURIComponent(id)}`,
  }),
  enclosure: Object.freeze({
    accessKeys: ["ENCLOSURE_MANAGEMENT"],
    category: "enclosures",
    destinationId: "collection.enclosure",
    icon: "enclosures",
    v1: (id) => `/EnclosureManagementView?enclosureId=${encodeURIComponent(id)}`,
    v2: (id) => `/v2/enclosures/${encodeURIComponent(id)}`,
  }),
  sector: Object.freeze({
    accessKeys: ["SECTOR_MANAGEMENT"],
    category: "sectors",
    destinationId: "collection.sector",
    icon: "sectors",
    v1: (id) => `/SectorManagementView?sectorId=${encodeURIComponent(id)}`,
    v2: (id) => `/v2/sectors/${encodeURIComponent(id)}`,
  }),
  campaign: Object.freeze({
    accessKeys: ["CAMPAIGN_MANAGEMENT"],
    category: "campaigns",
    destinationId: "welfare.campaign",
    icon: "campaigns",
    v1: (id) => `/CampaignManagementView?campaignId=${encodeURIComponent(id)}`,
    v2: (id) => `/v2/campaigns/${encodeURIComponent(id)}`,
  }),
  enrichment: Object.freeze({
    accessKeys: ["ENRICHMENT_MANAGEMENT"],
    category: "enrichments",
    destinationId: "enrichment.editor",
    icon: "enrichment",
    v1: (id) => `/EnrichManagementView?enrichId=${encodeURIComponent(id)}`,
    v2: (id) => `/v2/enrichments/editor/${encodeURIComponent(id)}`,
  }),
  group: Object.freeze({
    accessKeys: ["GROUP_MANAGEMENT"], category: "groups", destinationId: "collection.group", icon: "groups",
    v1: (id) => `/AdvancedGroupManagementView?groupId=${encodeURIComponent(id)}`,
    v2: (id) => `/v2/groups/${encodeURIComponent(id)}`,
  }),
  tour: Object.freeze({
    accessKeys: ["TOURNEE_MANAGEMENT"], category: "tours", destinationId: "operations.tour", icon: "tours",
    v1: (id) => `/TourneeManagementView?tourneeId=${encodeURIComponent(id)}`,
    v2: (id) => `/v2/tours/editor/${encodeURIComponent(id)}`,
  }),
  user: Object.freeze({
    accessKeys: ["USER_CREATION"], category: "users", destinationId: "account.settings", icon: "users",
    v1: (id) => `/userSettings?userId=${encodeURIComponent(id)}`,
    v2: (id) => `/v2/account?section=profile&userId=${encodeURIComponent(id)}`,
  }),
  taxon: Object.freeze({
    accessKeys: ["ANIMAL_MANAGEMENT"], category: "taxons", destinationId: "collection.individuals", icon: "animals",
    v1: (_id, document) => `/specieAnimalsListView?taxonomyType=${encodeURIComponent(document.taxonomy_type)}&taxonomyId=${encodeURIComponent(document.taxonomy_id)}`,
    v2: (_id, document) => `/v2/individuals?taxonomyType=${encodeURIComponent(document.taxonomy_type)}&taxonomyId=${encodeURIComponent(document.taxonomy_id)}`,
  }),
  questionnaire: Object.freeze({
    accessKeys: ["QUIZZ_MONITORING", "QUESTIONNAIRE_ADMIN"], category: "resources", destinationId: "resources.questionnaire-preview", icon: "questionnaires",
    v1: (id) => `/QuizzPreview2?surveyId=${encodeURIComponent(id)}`,
    v2: (id) => `/v2/resources/questionnaires/${encodeURIComponent(id)}/preview`,
  }),
  ethogram: Object.freeze({
    accessKeys: ["ETHO_MONITORING", "OBSERVATION_ADMIN"], category: "resources", destinationId: "resources.ethogram-preview", icon: "ethograms",
    v1: (id) => `/ObsPreview?obsId=${encodeURIComponent(id)}`,
    v2: (id) => `/v2/resources/ethograms/${encodeURIComponent(id)}/preview`,
  }),
  grid: Object.freeze({
    accessKeys: ["GRID_MANAGEMENT"], category: "resources", destinationId: "welfare.grid-detail", icon: "grids",
    v1: (id) => `/GridDetailView/${encodeURIComponent(id)}`,
    v2: (id) => `/v2/resources/grids/${encodeURIComponent(id)}/detail`,
  }),
  guide: Object.freeze({
    accessKeys: ["ENRICHMENT_GUIDES"], category: "resources", destinationId: "enrichment.guide", icon: "guides",
    v1: (id) => `/EnrichmentGuideDetailView/${encodeURIComponent(id)}`,
    v2: (id) => `/v2/enrichment-guides/${encodeURIComponent(id)}`,
  }),
  exercise: Object.freeze({
    accessKeys: ["TRAINING_EXERCISES"], category: "resources", destinationId: "training.exercises", icon: "training",
    v1: () => "/TrainingExercisesView",
    v2: () => "/v2/resources/exercises",
  }),
});

const normalizeVersion = (value) => (value === "v2" ? "v2" : "v1");

const normalizeSearchText = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase()
  .trim();

const matchRank = ({ context = [], label, query }) => {
  const normalizedQuery = normalizeSearchText(query);
  const normalizedLabel = normalizeSearchText(label);
  const normalizedContext = normalizeSearchText([label, ...context].flat().join(" "));
  if (normalizedLabel === normalizedQuery) return 0;
  if (normalizedLabel.startsWith(normalizedQuery)) return 1;
  if (normalizedContext.includes(normalizedQuery)) return 2;
  const terms = normalizedQuery.split(/\s+/).filter(Boolean);
  if (terms.length && terms.every((term) => normalizedContext.includes(term))) return 3;
  return 4;
};

const allowedEntityTypes = (accessKeys) => Object.entries(ENTITY_TYPES)
  .filter(([, definition]) => definition.accessKeys.some((key) => accessKeys?.has(key)))
  .map(([entityType]) => entityType);

const projectHit = (hit, version) => {
  const document = hit?.document;
  const definition = ENTITY_TYPES[document?.entity_type];
  if (!definition || !document.entity_id || !document.title) return null;
  return Object.freeze({
    id: `entity:${document.entity_type}:${document.entity_id}`,
    destinationId: definition.destinationId,
    label: document.title,
    href: definition[version](document.entity_id, document),
    icon: definition.icon,
    version,
    category: definition.category,
    description: document.subtitle || undefined,
    entityType: document.entity_type,
    entityId: document.entity_id,
    enclosureId: document.enclosure_id || undefined,
    enclosureName: document.enclosure_name || undefined,
  });
};

const decorateLocalResult = (result) => Object.freeze({
  ...result,
  category: "navigation",
});

const mergeRankedResults = ({ entities, local }) => {
  const merged = [];
  const seen = new Set();
  let entityIndex = 0;
  let localIndex = 0;
  const append = (candidate) => {
    if (!candidate || seen.has(candidate.result.href)) return;
    seen.add(candidate.result.href);
    merged.push(candidate.result);
  };

  while (localIndex < local.length || entityIndex < entities.length) {
    const localCandidate = local[localIndex];
    const entityCandidate = entities[entityIndex];
    if (!entityCandidate || (localCandidate && localCandidate.rank <= entityCandidate.rank)) {
      append(localCandidate);
      localIndex += 1;
    } else {
      append(entityCandidate);
      entityIndex += 1;
    }
  }
  return merged;
};

export const createUniversalSearchModule = ({ entitySearch }) => {
  if (!entitySearch || typeof entitySearch.search !== "function") {
    throw new TypeError("entitySearch.search must be a function");
  }

  return Object.freeze({
    async search({
      accessKeys,
      localResults = [],
      query = "",
      signal,
      version,
      zooId,
      userId,
    }) {
      const local = localResults
        .map((result, index) => ({
          index,
          rank: matchRank({
            context: [result.parentLabel, result.description],
            label: result.label,
            query,
          }),
          result: decorateLocalResult(result),
        }))
        .sort((left, right) => left.rank - right.rank || left.index - right.index);
      const normalizedQuery = String(query || "").trim();
      const entityTypes = allowedEntityTypes(accessKeys);
      const allowedEntityTypeSet = new Set(entityTypes);
      if (normalizedQuery.length < 2 || !zooId || entityTypes.length === 0) {
        return local.map(({ result }) => result);
      }

      try {
        const hits = await entitySearch.search({
          query: normalizedQuery,
          zooId,
          entityTypes,
          userId,
          signal,
        });
        const projected = hits
          .filter((hit) => allowedEntityTypeSet.has(hit?.document?.entity_type))
          .map((hit) => ({
            rank: matchRank({
              context: [
                hit?.document?.aliases,
                hit?.document?.subtitle,
                hit?.document?.enclosure_name,
                hit?.document?.sector_name,
              ],
              label: hit?.document?.title,
              query: normalizedQuery,
            }),
            result: projectHit(hit, normalizeVersion(version)),
          }))
          .filter(({ result }) => Boolean(result));
        return mergeRankedResults({ entities: projected, local });
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.warn("[UniversalSearch] Entity search unavailable; keeping navigation results", error);
        }
        return local.map(({ result }) => result);
      }
    },
  });
};

export { ENTITY_TYPES };

export default createUniversalSearchModule;
